import { NgClass, NgIf } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Injector, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-time-input',
  imports: [FormsModule, NgIf,NgClass],
  templateUrl: './time-input.component.html',
  // styleUrl: './time-input.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TimeInputComponent,
      multi: true
    }
  ],
  standalone: true,
})
export class TimeInputComponent implements ControlValueAccessor {
  @Input() data_list = [];
  @Input() label = '';
  @Input() placeholder = '';
  @Input() disabled: boolean = false;
  @Input() is_required: boolean = false;
  @Input() rounded: boolean = false;
  @Input() fetch: boolean = false;
  @Input() mask: boolean = false;
  @Input() text_type: boolean = true;
  @Input() type: 'text' | 'password' | 'email' | 'number' | 'text_number' | 'year_range' | 'hhmmss' = 'text_number';
  @Input() type_total_latter: any = 0;
  @Input() copyable: boolean = false;

  @Output() fetch_activated = new EventEmitter<any>();
  @Output() eye_activated = new EventEmitter<any>();

  paramValue: any;
  value = '';
  control: FormControl | any;

  // hhmmss three-part state
  hours_str = '';
  minutes_str = '';
  seconds_str = '';
  isFocused = false;
  timeError = '';

  @ViewChild('hoursInput') hoursInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('minutesInput') minutesInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('secondsInput') secondsInputRef!: ElementRef<HTMLInputElement>;

  get hasTimeValue(): boolean {
    return !!(this.hours_str || this.minutes_str || this.seconds_str);
  }

  onContainerClick(): void {
    if (this.disabled || this.paramValue == 'true') return;
    this.isFocused = true;
    setTimeout(() => this.hoursInputRef?.nativeElement?.focus(), 0);
  }

  onFocusOut(event: FocusEvent): void {
    const container = event.currentTarget as HTMLElement;
    const related = event.relatedTarget as HTMLElement | null;
    if (!related || !container.contains(related)) {
      this.isFocused = false;
    }
  }

  constructor(private injector: Injector, public route: ActivatedRoute) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.paramValue = params['view'];
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const ngControl: NgControl | null = this.injector.get(NgControl, null);
      if (ngControl) this.control = ngControl.control as FormControl;
    }, 100);
  }

  onTouched: any;
  onChange: (value: any) => void = () => {};

  writeValue(value: any): void {
    this.value = value || '';
    if (this.type === 'hhmmss') {
      if (value) {
        const parts = String(value).split(':');
        this.hours_str = parts[0] ? String(Number(parts[0])) : '';
        this.minutes_str = parts[1] ? String(Number(parts[1])) : '';
        this.seconds_str = parts[2] ? String(Number(parts[2])) : '';
      } else {
        this.hours_str = '';
        this.minutes_str = '';
        this.seconds_str = '';
      }
    }
  }

  onPartChange(): void {
    this.timeError = '';
    if (!this.hours_str && !this.minutes_str && !this.seconds_str) {
      this.onChange(null);
      return;
    }
    const h = Number(this.hours_str || '0');
    const m = Number(this.minutes_str || '0');
    const s = Number(this.seconds_str || '0');
    if (h > 24) {
      this.timeError = 'Hours cannot exceed 24';
      this.onChange(null);
      return;
    }
    if (m > 59) {
      this.timeError = 'Minutes cannot exceed 59';
      this.onChange(null);
      return;
    }
    if (s > 59) {
      this.timeError = 'Seconds cannot exceed 59';
      this.onChange(null);
      return;
    }
    if (h === 24 && (m !== 0 || s !== 0)) {
      this.timeError = 'When hours is 24, minutes and seconds must be 00';
      this.onChange(null);
      return;
    }
    const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    this.value = val;
    this.onChange(val);
  }

  validatePartInput(event: KeyboardEvent): void {
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }

  onPartInput(field: 'hours' | 'minutes'): void {
    if (field === 'hours' && this.hours_str.length === 2) {
      this.minutesInputRef?.nativeElement?.focus();
      this.minutesInputRef?.nativeElement?.select();
    } else if (field === 'minutes' && this.minutes_str.length === 2) {
      this.secondsInputRef?.nativeElement?.focus();
      this.secondsInputRef?.nativeElement?.select();
    }
  }

  onPartBackspace(event: KeyboardEvent, field: 'minutes' | 'seconds'): void {
    if (event.key !== 'Backspace') return;
    if (field === 'seconds' && !this.seconds_str) {
      event.preventDefault();
      this.minutesInputRef?.nativeElement?.focus();
      this.minutesInputRef?.nativeElement?.select();
    } else if (field === 'minutes' && !this.minutes_str) {
      event.preventDefault();
      this.hoursInputRef?.nativeElement?.focus();
      this.hoursInputRef?.nativeElement?.select();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  fetch_clicked() {
    this.fetch_activated.emit();
  }

  eye_clicked() {
    this.text_type = !this.text_type;
    this.eye_activated.emit(this.text_type);
  }

  copy_clicked() {
    if (this.value) navigator.clipboard.writeText(this.value);
  }

  // validateInput(event: KeyboardEvent): void {
  //   if (this.type === 'hhmmss') {
  //     // Only allow digits
  //     const allowedKeys = ['0','1','2','3','4','5','6','7','8','9'];
  //     if (!allowedKeys.includes(event.key)) {
  //       event.preventDefault();
  //       return;
  //     }

  //     setTimeout(() => {
  //       let val = this.value.replace(/[^0-9]/g, '');

  //       // Auto insert colons
  //       if (val.length > 2) val = val.slice(0,2) + ':' + val.slice(2);
  //       if (val.length > 5) val = val.slice(0,5) + ':' + val.slice(5,7);
  //       if (val.length > 8) val = val.slice(0,8);

  //       this.value = val;

  //       // Validate HH:MM:SS
  //       const regex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
  //       if (regex.test(val)) {
  //         this.onChange(this.value);
  //       } else {
  //         this.onChange(null);
  //       }
  //     });
  //   } else {
  //     // Existing validations for other types
  //     if (this.type === 'text') {
  //       if (!/^[A-Za-z ]$/.test(event.key)) event.preventDefault();
  //     } else if (this.type === 'number') {
  //       if (!/^[0-9]$/.test(event.key)) event.preventDefault();
  //     } else if (this.type === 'email') {
  //       if (!/^[A-Za-z0-9@._-]$/.test(event.key)) event.preventDefault();
  //     } else if (this.type === 'year_range') {
  //       if (!/^[0-9-]$/.test(event.key)) event.preventDefault();
  //     }
  //   }
  // }

  validateInput(event: KeyboardEvent): void {
  if (this.type === 'hhmmss') {

    // allow only digits
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    setTimeout(() => {
      let val = this.value.replace(/[^0-9]/g, '');

      // auto add :
      if (val.length > 2) val = val.slice(0, 2) + ':' + val.slice(2);
      if (val.length > 5) val = val.slice(0, 5) + ':' + val.slice(5, 7);
      if (val.length > 8) val = val.slice(0, 8);

      this.value = val;

      const regex = /^(\d{2}):(\d{2}):(\d{2})$/;
      if (!regex.test(val)) {
        this.onChange(null);
        return;
      }

      const [hh, mm, ss] = val.split(':').map(Number);

      // ⛔ hour > 24 not allowed
      if (hh > 24) {
        this.onChange(null);
        return;
      }

      // ⛔ if hour == 24 then mm & ss must be 00
      if (hh === 24 && (mm !== 0 || ss !== 0)) {
        this.onChange(null);
        return;
      }

      // ⛔ normal minute / second range
      if (mm > 59 || ss > 59) {
        this.onChange(null);
        return;
      }

      // ✅ valid
      this.onChange(val);
    });
  }
}

}
